import jsonExport from "jsonexport/dist";
import { downloadCSV, type Exporter } from "ra-core";

import { formatDate } from "../misc/formatDate";
import type {
  Company,
  ContractType,
  Sale,
  Sponsoring,
  SponsoringStage,
} from "../types";
import { findStageLabel } from "./sponsoringUtils";
import {
  sponsoringViewExportName,
  type SponsoringView,
} from "./sponsoringViews";

/** CSV column headers (Dutch, in this order) */
export const SPONSORINGS_EXPORT_COLUMNS = [
  "Bedrijf",
  "Contracttype",
  "Fase",
  "Einddatum",
  "Behandelaar",
  "Opmerkingen",
  "Aangemaakt op",
] as const;

export type SponsoringExportColumn =
  (typeof SPONSORINGS_EXPORT_COLUMNS)[number];

export type ExportedSponsoring = Record<
  Exclude<SponsoringExportColumn, "Contracttype">,
  string | undefined
> & { Contracttype?: string };

/** Balsponsoren have no contract type, so that column is left out entirely. */
export const exportColumnsForView = (
  view: SponsoringView,
): SponsoringExportColumn[] =>
  SPONSORINGS_EXPORT_COLUMNS.filter(
    (column) => view !== "ball" || column !== "Contracttype",
  );

/**
 * Flatten sponsorings into export rows, resolving the company, contract
 * type, stage label and owner name.
 */
export const toExportRows = (
  records: Sponsoring[],
  stages: SponsoringStage[],
  companies: Record<string | number, Company>,
  contractTypes: Record<string | number, ContractType>,
  sales: Record<string | number, Sale>,
  includeContractType = true,
): ExportedSponsoring[] =>
  records.map((sponsoring) => {
    const owner =
      sponsoring.sales_id != null ? sales[sponsoring.sales_id] : undefined;
    return {
      Bedrijf: companies[sponsoring.company_id]?.name,
      ...(includeContractType
        ? {
            Contracttype:
              sponsoring.contract_type_id != null
                ? contractTypes[sponsoring.contract_type_id]?.name
                : undefined,
          }
        : null),
      Fase: findStageLabel(stages, sponsoring.stage) ?? sponsoring.stage,
      Einddatum: sponsoring.end_date ? formatDate(sponsoring.end_date) : "",
      Behandelaar: owner ? `${owner.first_name} ${owner.last_name}` : undefined,
      Opmerkingen: sponsoring.description ?? undefined,
      "Aangemaakt op": formatDate(sponsoring.created_at),
    };
  });

export const createSponsoringExporter =
  (
    stages: SponsoringStage[],
    view: SponsoringView = "sponsorings",
  ): Exporter<Sponsoring> =>
  async (records, fetchRelatedRecords) => {
    const [companies, contractTypes, sales] = await Promise.all([
      fetchRelatedRecords<Company>(records, "company_id", "companies"),
      fetchRelatedRecords<ContractType>(
        records,
        "contract_type_id",
        "contract_types",
      ),
      fetchRelatedRecords<Sale>(records, "sales_id", "sales"),
    ]);

    const rows = toExportRows(
      records,
      stages,
      companies,
      contractTypes,
      sales,
      view !== "ball",
    );

    return jsonExport(
      rows,
      { headers: exportColumnsForView(view) },
      (_err: unknown, csv: string) => {
        downloadCSV(csv, sponsoringViewExportName[view]);
      },
    );
  };
